import {ListViewContentElementType} from "../../../enums/listViews/listViewContentElementType";
import {ListViewContentElementCapability} from "./listViewContentElementCapability";
import {ListViewContentElementControlType} from "../../../enums/listViews/listViewContentElementControlType";
import {ListViewContentElementDefaultLabel, ListViewContentElementDefaultXSize, ListViewContentElementLayoutType} from "../../../enums/listViews/listViewContentElementLayoutType";
import assertNever from "../../../../global/assetNever";
import {EnumHelper} from "../../../helpers/enumHelper";
import {SizeType} from "../../../enums/sizeType";
import ListViewContentElement from "./listViewContentElement";
import {ListViewIconSize} from "../listViewIconSize";
import {ViewLinkType} from "../viewLinkType";
import {AlignmentType} from "../alignmentType";
import { InputFormatType } from "../../../../formability/form_builder/models/inputFormatType";
import {ListFormatType} from '~/enums/listFormatType'

export default class ListViewContentElementCapabilityManager {
  static debug: boolean = false;

  static LABELABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementLayoutType.Column,
    ListViewContentElementControlType.CompositeText,
    ListViewContentElementLayoutType.ActionsColumn,
  ];

  static SELECTABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementLayoutType.Column,
    ListViewContentElementLayoutType.ActionsColumn,
    ListViewContentElementControlType.ModelProperty,
    ListViewContentElementControlType.Formula,
    ListViewContentElementControlType.IconSet,
    ListViewContentElementControlType.CompositeText,
  ];

  static X_SIZEABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementLayoutType.Column,
    ListViewContentElementLayoutType.ActionsColumn,
  ];

  static SORTED_BY_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementLayoutType.Column,
  ];

  static PROPERTY_REFERENCEABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementControlType.ModelProperty,
  ];

  static DERIVED_VALUABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementControlType.Formula,
  ];

  static ICON_IMAGE_REFERENCABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementControlType.IconSet,
  ];

  static VIEW_LINKABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementControlType.ModelProperty,
    ListViewContentElementControlType.CompositeText,
    ListViewContentElementControlType.Formula,
    ListViewContentElementControlType.IconSet,
  ];

  static FORMATTABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementControlType.ModelProperty,
  ];

  static ALIGNABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementControlType.ModelProperty,
  ];

  static LIST_FORMATTABLE_CONTROLS: ListViewContentElementType[] = [
    ListViewContentElementControlType.ModelProperty,
  ];

  static can(type: ListViewContentElementType, duty: ListViewContentElementCapability): boolean {
    let can: boolean = false;
    switch (duty) {
      case ListViewContentElementCapability.labelable:
        can = this.LABELABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.selectable:
        can = this.SELECTABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.xSizeable:
        can = this.X_SIZEABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.sortedByable:
        can = this.SORTED_BY_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.propertyReferenceable:
        can = this.PROPERTY_REFERENCEABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.derivedValuable:
        can = this.DERIVED_VALUABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.iconImageReferencable:
        can = this.ICON_IMAGE_REFERENCABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.viewLinkable:
        can = this.VIEW_LINKABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.formattable:
        can = this.FORMATTABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.alignable:
        can = this.ALIGNABLE_CONTROLS.includes(type);
        break;
      case ListViewContentElementCapability.listFormattable:
        can = this.LIST_FORMATTABLE_CONTROLS.includes(type);
        break;
      default:
        assertNever(duty);
        can = false;
        break;
    }
    this.log(`Type ${ListViewContentElementControlType[type] || ListViewContentElementLayoutType[type]} ${can ? 'can' : 'can\'t'} ${ListViewContentElementCapability[duty]}`);
    return can;
  }

  static addDefaultProperties(m: ListViewContentElement): void {
    // done this way so a case statement can use assertNever to ensure all capabilities are accounted for
    EnumHelper.toArray(ListViewContentElementCapability).forEach((c: ListViewContentElementCapability) => {
      switch (c) {
        case ListViewContentElementCapability.labelable:
          m.defineProperty('label_title', ListViewContentElementDefaultLabel[m.content_type]);
          break;
        case ListViewContentElementCapability.selectable:
          // Do nothing
          break;
        case ListViewContentElementCapability.xSizeable:
          m.defineProperty('x_size', ListViewContentElementDefaultXSize[m.content_type] || SizeType.Sm);
          break;
        case ListViewContentElementCapability.sortedByable:
          m.defineProperty('sorted_by_ids', []);
          break;
        case ListViewContentElementCapability.propertyReferenceable:
          m.defineProperty('property_id', null);
          m.defineProperty('property_client_id', null);
          m.defineProperty('builtin_property_type', null)
          m.defineProperty('date_display_format', null);
          break;
        case ListViewContentElementCapability.derivedValuable:
          m.defineProperty('derived_value', null);
          m.defineProperty('derived_value_formula', null);
          break;
        case ListViewContentElementCapability.iconImageReferencable:
          m.defineProperty('icon_size', ListViewIconSize.medium);
          break;
        case ListViewContentElementCapability.viewLinkable:
          m.defineProperty('linked_view_type', ViewLinkType.none);
          m.defineProperty('linked_view_id', null);
          break;
        case ListViewContentElementCapability.formattable:
          let formatType = InputFormatType.None;
          m.defineProperty('format_type', formatType)
          break;
        case ListViewContentElementCapability.alignable:
          m.defineProperty('alignment_type', AlignmentType.left)
          break;
        case ListViewContentElementCapability.listFormattable:
          m.defineProperty('list_format_type', ListFormatType.Comma)
          break;
        default:
          assertNever(c);
          break;
      }
    });
  }

  protected static log(message: string) {
    if (this.debug) {
      console.log(`DEBUG: ListViewContentElementCapabilityManager: ${message}`);
    }
  }
}